import * as Linking from 'expo-linking';
import * as Updates from 'expo-updates';
import { useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import {
  trackUpdateAction,
  trackUpdateCompleted,
  trackUpdateFailed,
  trackUpdatePhase,
  UPDATE_ACTIONS,
} from '@/analytics/update';
import { AwarenessLogo } from '@/components/AwarenessLogo';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useColors } from '@/hooks/useColors';
import { getUserAttributes } from '@/services/auth';
import { env } from '@/util/env';
import { Sentry } from '@/util/sentry';
import { safeCheckAndFetchUpdate } from '@/util/updateHelpers';

type UpdateKind = 'ota' | 'store';

interface RequiredUpdateModalProps {
  isOpen: boolean;
  updateType: UpdateKind;
  message?: string;
  isChecking?: boolean;
  isDownloading?: boolean;
  isUpdatePending?: boolean;
  currentVersion?: string;
  latestVersion?: string;
  onSuccess?: () => void;
  onPress?: () => void;
}

/**
 * Full-screen blocking modal for required updates. Cannot be dismissed.
 *
 * OTA: runs the internal download flow with stage-based button text
 * (Checking, Downloading, Installing, Reloading).
 * Store: opens the platform store via Linking.
 *
 * Non-required updates use UpdateActionsheet.
 */
export function RequiredUpdateModal({
  isOpen,
  updateType,
  message,
  isChecking = false,
  isDownloading = false,
  isUpdatePending = false,
  currentVersion,
  latestVersion,
  onSuccess,
  onPress,
}: RequiredUpdateModalProps) {
  const { colors } = useColors();

  const [isFetching, setIsFetching] = useState(false);

  const [isApplying, setIsApplying] = useState(false);

  const [isReloading, setIsReloading] = useState(false);

  const isStoreUpdate = updateType === 'store';

  const platformStoreName = Platform.OS === 'ios' ? 'App Store' : 'Google Play';

  const handleStoreUpdate = () => {
    if (!currentVersion) {
      return;
    }

    const bundleId = env.EXPO_PUBLIC_APP_BUNDLE_ID;

    const appStoreId = env.EXPO_PUBLIC_APP_STORE_CONNECT_ID;

    const storeUrl =
      Platform.OS === 'ios'
        ? `itms-apps://apps.apple.com/us/app/primum/id${appStoreId}`
        : `market://details?id=${bundleId}`;

    const fallbackUrl =
      Platform.OS === 'ios'
        ? `https://apps.apple.com/us/app/primum/id${appStoreId}`
        : `https://play.google.com/store/apps/details?id=${bundleId}`;

    trackUpdateAction({
      updateType: 'store',
      action: UPDATE_ACTIONS.OPEN_STORE,
      currentVersion,
      userInitiated: true,
      additionalProperties: {
        platform: Platform.OS,
        latestVersion,
        required: true,
        storeUrl,
        fallbackUrl,
      },
    });

    Linking.openURL(storeUrl).catch(() => {
      trackUpdateAction({
        updateType: 'store',
        action: UPDATE_ACTIONS.OPEN_STORE_FALLBACK,
        currentVersion,
        userInitiated: true,
        additionalProperties: {
          platform: Platform.OS,
          latestVersion,
          required: true,
          usedUrl: fallbackUrl,
          primaryUrl: storeUrl,
        },
      });

      Linking.openURL(fallbackUrl).catch(() => {});
    });
  };

  const handleOtaUpdate = async () => {
    if (isFetching || isApplying || isReloading || !currentVersion) {
      return;
    }

    trackUpdateAction({
      updateType: 'ota',
      action: UPDATE_ACTIONS.CONFIRM,
      currentVersion,
      userInitiated: true,
      additionalProperties: {
        platform: Platform.OS,
        required: true,
      },
    });

    setIsFetching(true);

    Sentry.addBreadcrumb({
      category: 'ota-update',
      message: 'Required OTA update started',
      level: 'info',
      data: { currentVersion, isUpdatePending },
    });

    trackUpdatePhase({
      updateType: 'ota',
      currentVersion,
      required: true,
      phase: 'download',
      status: 'started',
    });

    try {
      let username: string | undefined;

      try {
        const userAttributes = await getUserAttributes();

        const firstName = userAttributes['custom:first_name'];

        const lastName = userAttributes['custom:last_name'];

        username = firstName && lastName ? `${firstName} ${lastName}` : undefined;
      } catch (authError) {
        Sentry.addBreadcrumb({
          category: 'ota-update',
          message: 'Could not fetch user attributes for required update',
          level: 'warning',
          data: {
            error: authError instanceof Error ? authError.message : String(authError),
          },
        });

        username = undefined;
      }

      const updateStartTime = Date.now();

      const { isAvailable, fetchedSuccessfully, error } = await safeCheckAndFetchUpdate();

      const updateDurationMs = Date.now() - updateStartTime;

      await trackUpdateCompleted('ota', currentVersion, true, false, {
        username,
        updateWasAlreadyDownloading: isDownloading,
        updateWasAvailable: isAvailable,
        updateFetchedSuccessfully: fetchedSuccessfully,
        updateDurationMs,
        hadError: !!error,
        required: true,
      });

      if (error) {
        throw new Error(error);
      }

      setIsFetching(false);

      setIsApplying(true);

      onSuccess?.();

      if (fetchedSuccessfully || isUpdatePending) {
        setIsReloading(true);

        Sentry.addBreadcrumb({
          category: 'ota-update',
          message: 'Required OTA reload initiated',
          level: 'info',
          data: { currentVersion, fetchedSuccessfully, isUpdatePending },
        });

        trackUpdatePhase({
          updateType: 'ota',
          currentVersion,
          required: true,
          phase: 'reload',
          status: 'started',
        });

        await new Promise((resolve) => setTimeout(resolve, 400));

        await Updates.reloadAsync();
      }
    } catch (caught) {
      Sentry.captureException(caught, {
        tags: {
          component: 'RequiredUpdateModal',
          source: 'ota-update',
        },
        extra: {
          errorMessage: caught instanceof Error ? caught.message : 'Unknown error',
          currentVersion,
          isUpdatePending,
          isFetching,
          isApplying,
          isReloading,
        },
      });

      await trackUpdateFailed(
        'ota',
        currentVersion,
        caught instanceof Error ? caught.message : 'Unknown error',
        true,
        false,
        {
          required: true,
        }
      );
    } finally {
      setIsFetching(false);

      setIsApplying(false);

      setIsReloading(false);
    }
  };

  const handleUpdate = async () => {
    if (isStoreUpdate) {
      handleStoreUpdate();
    } else {
      await handleOtaUpdate();
    }
  };

  const isOtaInProgress =
    !isStoreUpdate && (isFetching || isDownloading || isApplying || isReloading || isChecking);

  const getDescription = (): string => {
    if (message) {
      return message;
    }

    if (isStoreUpdate) {
      return latestVersion
        ? `Version ${latestVersion} of the Primum app is available on ${platformStoreName}.`
        : `A new version of the Primum app is available on ${platformStoreName}.`;
    }

    return 'A new update is required to continue using the app.';
  };

  const getButtonText = (): string => {
    if (isStoreUpdate) {
      return `Open ${platformStoreName}`;
    }

    if (isReloading) {
      return 'Reloading…';
    }

    if (isApplying) {
      return 'Installing…';
    }

    if (isDownloading) {
      return 'Downloading…';
    }

    if (isChecking || (isFetching && !isDownloading && !isUpdatePending)) {
      return 'Checking…';
    }

    return 'Update now';
  };

  return (
    <View style={[StyleSheet.absoluteFill, { zIndex: 999 }]}>
      <SafeAreaView className="flex-1 bg-primary-500">
        <VStack className="flex-1 justify-between px-6 py-12">
          <VStack className="items-center pt-6">
            <AwarenessLogo alt="Primum logo" kind="reverse" box={{ height: 52, width: 200 }} />
          </VStack>

          <VStack space="md" className="flex-1 items-center justify-center px-4">
            <Heading size="h1-xl" className="text-center text-white">
              New Version Available
            </Heading>

            <Text size="lg" className="text-center text-white/90">
              {getDescription()}
            </Text>
          </VStack>

          <Button
            className="w-full bg-white data-[hover=true]:bg-primary-100 data-[active=true]:bg-primary-700"
            onPress={onPress ?? handleUpdate}
            disabled={isOtaInProgress}
            action="primary">
            <HStack space="sm" className="items-center">
              {isOtaInProgress ? <Spinner size="small" color={colors.primary[600]} /> : null}

              <ButtonText className="text-primary-600">{getButtonText()}</ButtonText>
            </HStack>
          </Button>
        </VStack>
      </SafeAreaView>
    </View>
  );
}
